import type { GetError } from "@dealer-center/core-components";
import { defaultDepartments } from "./defaultDepartments";

/**
 * Validates department label to prevent duplicates and name collisions.
 *
 * @param existingTypes - Array of existing department types (uppercase)
 * @param defaultDepartmentTypes - Array of pre-configured department types (uppercase)
 * @returns Validation function compatible with TextInput's getError prop
 */
export const validateDepartment = (
  existingTypes: string[],
  defaultDepartmentTypes: string[]
): GetError => {
  return (_label, value) => {
    // Allow empty values
    if (!value || value.trim().length === 0) {
      return {
        triggered: false,
        validationState: {
          isValid: true,
          type: "success"
        }
      };
    }

    const normalizedValue = value.trim();

    if (normalizedValue.length > 50) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Department name cannot exceed 50 characters"
        }
      };
    }

    // Validate allowed characters (letters, numbers, apostrophe, spaces)
    const allowedCharsRegex = /^[a-zA-Z0-9'\s]+$/;
    if (!allowedCharsRegex.test(normalizedValue)) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Only letters, numbers, apostrophes, and spaces are allowed"
        }
      };
    }

    // Generate the type from the label
    const generatedType = labelToType(normalizedValue);

    // Check collision with existing departments
    if (existingTypes.includes(generatedType)) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Department already exists"
        }
      };
    }

    // Check collision with pre-configured department types
    if (defaultDepartmentTypes.includes(generatedType)) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Cannot use a pre-configured department name"
        }
      };
    }

    // Check collision with pre-configured department labels (case-insensitive)
    const defaultLabels = defaultDepartments.map(d => d.label.toUpperCase());
    if (defaultLabels.includes(normalizedValue.toUpperCase())) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Cannot use a pre-configured department name"
        }
      };
    }

    return {
      triggered: false,
      validationState: {
        isValid: true,
        type: "success"
      }
    };
  };
};

/**
 * Converts a department label to a valid type key.
 * Transforms human-readable labels into consistent uppercase type identifiers.
 *
 * Transformation rules:
 * - Trims leading/trailing whitespace
 * - Converts to uppercase
 * - Replaces spaces with underscores
 * - Removes non-alphanumeric characters (except underscores)
 * - Collapses consecutive underscores
 * - Removes leading/trailing underscores
 *
 * @param label - The department label to convert
 * @returns Uppercase type string suitable for database storage
 *
 * @example
 * labelToType("Body Shop") => "BODY_SHOP"
 * labelToType("Sales & Service") => "SALES_SERVICE"
 * labelToType("  Custom Dept  ") => "CUSTOM_DEPT"
 * labelToType("Mike's Auto") => "MIKES_AUTO"
 * labelToType("Service - Parts") => "SERVICE_PARTS"
 * labelToType("___Special___") => "SPECIAL"
 */
export const labelToType = (label: string): string => {
  if (!label) return "";

  return (
    label
      .trim()
      .toUpperCase()
      // Replace spaces with underscores
      .replace(/\s+/g, "_")
      // Remove non-alphanumeric characters except underscores
      .replace(/[^A-Z0-9_]/g, "")
      // Remove consecutive underscores
      .replace(/_+/g, "_")
      // Remove leading/trailing underscores
      .replace(/^_+|_+$/g, "")
  );
};
